import React from "react";
import styled from "styled-components";
import { TASKBAR_HEIGHT } from "../Taskbar";

// A world-positioned, optionally draggable wrapper.
//
// One Layout element with transform
// `translate(-50%, -50%) translate(x + dx, y + dy)`. The `-50%` sizes
// against Layout's own box, which matches the child's size, so the child's
// center lands at (x + dx, y + dy) without us measuring anything. The
// optional `transition` prop drives CSS transitions so layout phase
// changes (wide → focused) animate; callers drop it once the layout is
// static so the compositor layer goes away and drag feels instant.
//
// Drag handle: any descendant with `data-drag-handle` (Window's title
// bar sets this). Buttons inside a handle still click normally — we skip
// drag initiation when the mousedown target is a button.

// Our transforms live inside #root, which has `zoom: var(--ui-zoom)`.
// clientX/clientY from mouse events are in unzoomed viewport CSS pixels,
// so we divide by the zoom factor to get the matching translate amount.
// Read each drag start (cheap; constant in practice but tracks any runtime
// change to the variable).
function readUiZoom() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(
    "--ui-zoom",
  );
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function PositionedWindow({
  position,
  opacity = 1,
  draggable = false,
  transition,
  zIndex,
  onActivate,
  // Stamped as data-window-box so the Stage can measure this window
  // for the minimize/restore zoom-rectangle effect.
  boxId,
  // Fully non-interactive (used for faded-out windows so invisible
  // neighbors can't swallow clicks or be dragged).
  inert = false,
  children,
}) {
  const [drag, setDrag] = React.useState({ dx: 0, dy: 0 });
  const layoutRef = React.useRef(null);
  // Live handlers for the current drag session, so we can detach on
  // unmount if the user never releases (React strict-mode remounts,
  // parent unmounts mid-drag, etc.).
  const sessionRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      const s = sessionRef.current;
      if (!s) return;
      window.removeEventListener("pointermove", s.onMove);
      window.removeEventListener("pointerup", s.onUp);
      window.removeEventListener("pointercancel", s.onUp);
      sessionRef.current = null;
    };
  }, []);

  // Pointer events, not mouse events: fingers drag windows too (the
  // handle sets touch-action: none below so the page doesn't scroll
  // out from under a title-bar drag).
  const onPointerDown = (e) => {
    // Any press claims focus (desktop-style raise), even ones that
    // don't start a drag.
    if (onActivate) onActivate();
    if (!draggable) return;
    if (e.button !== 0) return;
    if (e.target.closest("button, input, [data-no-drag]")) return;
    if (!e.target.closest("[data-drag-handle]")) return;
    e.preventDefault();

    const startX = e.clientX;
    const startY = e.clientY;
    const baseDx = drag.dx;
    const baseDy = drag.dy;
    const zoom = readUiZoom();

    // Real-Windows drag clamp: the title bar can never go above the top
    // of the screen or under the taskbar, and at least a hand's width
    // of the window stays on screen horizontally — a window can hang
    // off, but never become unreachable. Coordinates: our (dx,dy) are
    // window-CENTER offsets from the viewport center.
    const rect = layoutRef.current?.getBoundingClientRect();
    const winW = rect ? rect.width / zoom : 620;
    const winH = rect ? rect.height / zoom : 435;
    const clamp = (dx, dy) => {
      const W = window.innerWidth / zoom;
      const H = window.innerHeight / zoom;
      const GRAB_X = 60;
      let cx = W / 2 + position.x + dx;
      let cy = H / 2 + position.y + dy;
      cx = Math.max(GRAB_X - winW / 2, Math.min(W - GRAB_X + winW / 2, cx));
      const minCy = winH / 2; // top edge can't cross the screen top
      const maxCy = H - TASKBAR_HEIGHT - 24 + winH / 2;
      cy = Math.max(minCy, Math.min(maxCy, cy));
      return { dx: cx - W / 2 - position.x, dy: cy - H / 2 - position.y };
    };

    // Imperative during the drag: write the CSS vars straight onto the
    // node per mousemove so the window rides the cursor with zero React
    // lag; commit to state once on mouseup so React's world agrees.
    let liveDx = baseDx;
    let liveDy = baseDy;
    const onMove = (ev) => {
      const clamped = clamp(
        baseDx + (ev.clientX - startX) / zoom,
        baseDy + (ev.clientY - startY) / zoom,
      );
      liveDx = clamped.dx;
      liveDy = clamped.dy;
      const el = layoutRef.current;
      if (el) {
        el.style.setProperty("--dx", position.x + liveDx + "px");
        el.style.setProperty("--dy", position.y + liveDy + "px");
      }
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      sessionRef.current = null;
      setDrag({ dx: liveDx, dy: liveDy });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    sessionRef.current = { onMove, onUp };
  };

  const layoutStyle = {
    "--dx": position.x + drag.dx + "px",
    "--dy": position.y + drag.dy + "px",
    "--opacity": opacity,
  };

  if (transition) {
    layoutStyle["--transition"] = transition;
  }
  if (zIndex != null) {
    layoutStyle.zIndex = zIndex;
  }
  if (inert) {
    layoutStyle.pointerEvents = "none";
  }

  return (
    <Layout
      ref={layoutRef}
      onPointerDown={onPointerDown}
      style={layoutStyle}
      data-window-box={boxId}
      data-draggable={draggable && !inert ? "true" : undefined}
    >
      {children}
    </Layout>
  );
}

const Layout = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  transform: translate(-50%, -50%) translate(var(--dx), var(--dy));
  opacity: var(--opacity);
  transition: var(--transition, none);

  /* A finger on the title bar drags the WINDOW, never scrolls the
     page (touch pointermove would otherwise be eaten by scrolling). */
  & [data-drag-handle] {
    touch-action: none;
  }
`;

export default PositionedWindow;
